import { autoUpdate, flip, FloatingPortal, offset, shift, useFloating } from '@floating-ui/react'
import { useAtomValue } from 'jotai'
import { useState } from 'react'
import { externalCanvasDataAtom, layerAtomFamily, selectedLayerIdAtom } from '../../atoms'
import { useCanvasStore } from '../../canvas-store'
import type { Background } from '../../data/v1'
import { FOCUSABLE_LAYER_TYPES } from '../constants'
import { useLayerElement } from '../use-layer-element'
import { ImageQuickTools } from './image'
import { ImageCropQuickTool } from './image-crop'
import { TextQuickTools } from './text'

/**
 * Fallback for layer types that don't have their own quick-tools file yet.
 * Shows image crop if the layer has an image background, otherwise a dash.
 */
function GenericQuickTools(
	{ layerId, background, externalBackgroundUrl, layerEl }: {
		layerId: string
		background: Background | null
		externalBackgroundUrl: string
		layerEl: HTMLElement | null
	},
) {
	const [cropActive, setCropActive] = useState(false)

	if (!background) {
		return <span className="px-1 text-xs text-grey-400">&mdash;</span>
	}

	return (
		<ImageCropQuickTool
			layerId={layerId}
			active={cropActive}
			onToggle={() => setCropActive(!cropActive)}
			background={background}
			externalBackgroundUrl={externalBackgroundUrl}
			layerEl={layerEl}
		/>
	)
}

export function GizmoQuickTools() {
	const store = useCanvasStore()
	const layerId = useAtomValue(selectedLayerIdAtom, { store })
	const layer = useAtomValue(layerAtomFamily(layerId ?? ''), { store })
	const externalData = useAtomValue(externalCanvasDataAtom, { store })

	const visible = layerId != null
		&& layer != null && !layer.hidden && !layer.locked
		&& FOCUSABLE_LAYER_TYPES.has(layer.type)

	const referenceEl = useLayerElement(visible ? layerId : null, [layer])

	const { refs, floatingStyles } = useFloating({
		placement: 'bottom',
		middleware: [offset(6), flip({ padding: 8 }), shift({ padding: 8 })],
		elements: { reference: referenceEl },
		whileElementsMounted: (...args) => autoUpdate(...args, { animationFrame: true }),
	})

	if (!visible || !referenceEl || !layer || !layerId) { return null }

	function renderQuickTools() {
		switch (layer!.type) {
			case 'text':
				return (
					<TextQuickTools
						layer={layer as Extract<typeof layer, { type: 'text' }>}
						layerEl={referenceEl}
					/>
				)
			case 'image':
				return (
					<ImageQuickTools
						layer={layer as Extract<typeof layer, { type: 'image' }>}
						layerEl={referenceEl}
					/>
				)
			default:
				return (
					<GenericQuickTools
						layerId={layerId!}
						background={'background' in layer! ? layer!.background : null}
						externalBackgroundUrl={externalData.backgroundUrl}
						layerEl={referenceEl}
					/>
				)
		}
	}

	return (
		<FloatingPortal>
			<div
				key={layerId}
				ref={refs.setFloating}
				data-render-exclude
				style={{ ...floatingStyles, pointerEvents: 'auto' }}
				className="flex items-center gap-0.5 rounded-md border border-grey-200 bg-white px-1 py-0.5 shadow-sm"
			>
				{renderQuickTools()}
			</div>
		</FloatingPortal>
	)
}
